import ChapterBadge from "./ChapterBadge";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Sparkle } from "@phosphor-icons/react";

const WhereAIBegins = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const statements = [
    {
      text: "AI value depends on real enterprise context, not just model access.",
    },
    {
      text: "Before broad rollout, leadership needs to know what systems and signals can support AI today.",
    },
    {
      text: "OnTenacity turns that understanding into an immediate executive intelligence layer.",
    },
  ];

  return (
    <section 
      ref={ref}
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 relative bg-[#0A0A0B]"
      data-testid="where-ai-begins-section"
    >
      <div className="relative z-10 max-w-5xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <ChapterBadge number="01" name="Foundation" />
          <span className="mono-label flex items-center justify-center gap-2">
            <Sparkle size={14} className="text-[#00E5FF]" />
            The AI Starting Point
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Outfit']">
            Where enterprise AI <span className="gradient-text">should begin</span>
          </h2>
          <p className="mt-6 text-[#A1A1AA] max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            Most companies start the AI journey in the wrong place — with tools, pilots, or hype. 
            OnTenacity helps leadership start with clarity by identifying what the current enterprise 
            can actually support, trust, and activate first.
          </p>
        </motion.div>

        {/* Statement Cards */}
        <div className="space-y-4">
          {statements.map((statement, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="p-6 md:p-8 rounded-xl bg-[#0F0F10] border border-white/5 hover:border-[#00E5FF]/20 transition-colors"
              data-testid={`ai-statement-card-${index + 1}`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center">
                  <span className="text-sm font-mono text-[#00E5FF]">{index + 1}</span>
                </div>
                <p className="text-lg md:text-xl text-white font-['Outfit'] font-medium leading-relaxed">
                  {statement.text}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhereAIBegins;
